// Turns raw API error codes into sentences a non-technical operator can act on.
//
// The API's error envelope is `{ error: "<code>" }` (see api/src/middleware/error.ts),
// where <code> is the HTTPException message — machine-readable strings like
// `invalid_admin_credentials` or `rate_limit_exceeded`. Surfacing those verbatim is
// what produced the 2026-07-22 report: the Zoki widget answered "[Fehler]
// unauthenticated" three times and the login form showed a bare
// `invalid_admin_credentials`, neither of which tells the user what to DO.
//
// Rule: a known code becomes a full sentence naming the real cause and the next
// step. An UNKNOWN code still gets a human sentence, but keeps the raw code in
// parentheses so support can diagnose it without a screenshot of the console.

// Structural type — matches how the codebase calls `t('key', 'German fallback')`.
export type TFunc = (key: string, defaultValue: string) => string;

const CODE_MAP: Record<string, { key: string; de: string }> = {
  // ── Bestandsabgleich mit der Seite (/site-listings) ──
  duplicate_client: {
    key: 'errors.duplicate_client',
    de: 'Dieser Verkäufer ist bereits im CRM angelegt. Bitte prüfen Sie den bestehenden Eintrag.',
  },
  site_listing_not_found: {
    key: 'errors.site_listing_not_found',
    de: 'Dieses Inserat wurde nicht gefunden.',
  },
  not_listing_agent: {
    key: 'errors.not_listing_agent',
    de: 'Nur der Makler dieses Inserats oder die Administration kann es ins CRM übernehmen.',
  },
  listing_agent_required: {
    key: 'errors.listing_agent_required',
    de: 'Für dieses Inserat ist kein Makler erkennbar. Bitte wählen Sie den zuständigen Makler aus.',
  },
  listing_agent_invalid: {
    key: 'errors.listing_agent_invalid',
    de: 'Bitte wählen Sie einen aktiven Makler aus.',
  },
  listing_agent_mismatch: {
    key: 'errors.listing_agent_mismatch',
    de: 'Dieses Inserat gehört einem anderen Makler. Er wird automatisch zuständig.',
  },
  listing_has_open_proposal: {
    key: 'errors.listing_has_open_proposal',
    de: 'Zu diesem Inserat gibt es offene Vorschläge. Die Administration entscheidet zuerst, ob es ein Objekt im CRM schon gibt.',
  },
  listing_already_linked: {
    key: 'errors.listing_already_linked',
    de: 'Dieses Inserat ist bereits mit einem Objekt im CRM verbunden.',
  },
  property_already_linked: {
    key: 'errors.property_already_linked',
    de: 'Dieses Objekt ist bereits mit einem anderen Inserat verbunden.',
  },
  listing_not_linked: {
    key: 'errors.listing_not_linked',
    de: 'Dieses Inserat ist mit keinem Objekt verbunden.',
  },
  listing_gone: {
    key: 'errors.listing_gone',
    de: 'Dieses Inserat steht nicht mehr auf der Website.',
  },
  invalid_phone_format: {
    key: 'errors.invalid_phone_format',
    de: 'Bitte geben Sie eine gültige Telefonnummer an.',
  },
  title_required: {
    key: 'errors.title_required',
    de: 'Bitte wählen Sie die Anrede aus.',
  },
  last_name_required: {
    key: 'errors.last_name_required',
    de: 'Bitte geben Sie den Nachnamen an.',
  },
  property_not_found: {
    key: 'errors.property_not_found',
    de: 'Dieses Objekt wurde nicht gefunden.',
  },
  invalid_id: {
    key: 'errors.invalid_id',
    de: 'Dieser Eintrag ist ungültig. Bitte laden Sie die Seite neu.',
  },
  listing_city_required: {
    key: 'errors.listing_city_required',
    de: 'Bitte geben Sie den Ort des Objekts an.',
  },

  // ── session / permissions ──
  unauthenticated: {
    key: 'errors.session_expired',
    de: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
  },
  forbidden: {
    key: 'errors.forbidden',
    de: 'Dafür fehlt Ihnen die Berechtigung.',
  },

  // ── login (POST /auth/agent) ──
  invalid_admin_credentials: {
    key: 'errors.invalid_admin_credentials',
    de: 'Benutzername oder Passwort ist falsch.',
  },
  // Better-Auth's own 401 body for a wrong password. Now the DOMINANT failure
  // mode on the e-mail path (which, unlike the Agent-ID path, has no
  // default-password fallback), so it must not surface as a raw code.
  invalid_credentials: {
    key: 'errors.invalid_credentials',
    de: 'E-Mail-Adresse bzw. Agent-ID oder Passwort ist falsch.',
  },
  INVALID_EMAIL_OR_PASSWORD: {
    key: 'errors.invalid_credentials',
    de: 'E-Mail-Adresse bzw. Agent-ID oder Passwort ist falsch.',
  },
  agent_not_found: {
    key: 'errors.agent_not_found',
    de: 'Diese Agent-ID bzw. E-Mail-Adresse ist nicht registriert. Bitte prüfen Sie die Eingabe.',
  },
  password_required_for_email: {
    key: 'errors.password_required_for_email',
    de: 'Bei der Anmeldung mit E-Mail-Adresse ist ein Passwort erforderlich.',
  },
  email_belongs_to_admin_use_username: {
    key: 'errors.email_belongs_to_admin_use_username',
    de: 'Diese E-Mail-Adresse gehört zu einem Admin-Konto. Bitte melden Sie sich über den Reiter „Admin" mit Ihrem Benutzernamen an.',
  },
  email_ambiguous: {
    key: 'errors.email_ambiguous',
    de: 'Diese E-Mail-Adresse ist mehreren Konten zugeordnet. Bitte melden Sie sich mit Ihrer Agent-ID an.',
  },
  email_login_requires_first_agent_id_login: {
    key: 'errors.email_login_requires_first_agent_id_login',
    de: 'Bitte melden Sie sich zuerst einmalig mit Ihrer Agent-ID an. Danach funktioniert auch die E-Mail-Adresse.',
  },
  agent_inactive: {
    key: 'errors.agent_inactive',
    de: 'Dieses Konto ist deaktiviert. Bitte wenden Sie sich an die Administration.',
  },
  password_required_for_admin: {
    key: 'errors.password_required_for_admin',
    de: 'Für die Admin-Anmeldung ist ein Passwort erforderlich.',
  },
  user_record_missing: {
    key: 'errors.user_record_missing',
    de: 'Der Konto-Datensatz fehlt. Bitte wenden Sie sich an die Administration.',
  },

  // ── password change (POST /auth/me/password) ──
  invalid_current_password: {
    key: 'errors.invalid_current_password',
    de: 'Das aktuelle Passwort ist falsch.',
  },
  new_password_must_differ: {
    key: 'errors.new_password_must_differ',
    de: 'Das neue Passwort muss sich vom aktuellen unterscheiden.',
  },
  no_credential_account: {
    key: 'errors.no_credential_account',
    de: 'Für dieses Konto ist kein Passwort hinterlegt. Bitte wenden Sie sich an die Administration.',
  },

  // ── Dokument zum Klienten bringen ──
  pdf_not_generated: {
    key: 'errors.pdf_not_generated',
    de: 'Für dieses Dokument wurde noch keine PDF erzeugt — beim Termin auf „generieren", dann kann es zum Klienten.',
  },
  document_already_signed: {
    key: 'errors.document_already_signed',
    de: 'Dieses Dokument ist bereits unterschrieben.',
  },
  recipient_missing: {
    key: 'errors.recipient_missing',
    de: 'Für diesen Klienten ist keine passende Adresse hinterlegt (E-Mail bzw. Telefonnummer).',
  },
  document_archived: {
    key: 'errors.document_archived',
    de: 'Dieses Dokument ist gelöscht.',
  },

  // ── claim / exclusivity (Preuzmi-klijenta) ──
  client_contract_locked: {
    key: 'errors.client_contract_locked',
    de: 'Vertrag unterschrieben — dieser Klient ist exklusiv beim zuständigen Makler.',
  },
  client_already_claimed: {
    key: 'errors.client_already_claimed',
    de: 'Dieser Klient ist bereits übernommen.',
  },
  pool_client_already_claimed: {
    key: 'errors.pool_client_already_claimed',
    de: 'Dieser Klient wurde bereits von jemand anderem übernommen.',
  },
  not_claimable: {
    key: 'errors.not_claimable',
    de: 'Dieser Klient kann nicht übernommen werden (nur Verkäufer und Pool-Klienten).',
  },
  client_closed: {
    key: 'errors.client_closed',
    de: 'Dieser Verkäufer ist geschlossen (verkauft/zurückgezogen) — keine weiteren Aktionen möglich.',
  },
  client_already_closed: {
    key: 'errors.client_already_closed',
    de: 'Dieser Verkäufer wurde bereits geschlossen.',
  },
  client_not_closed: {
    key: 'errors.client_not_closed',
    de: 'Dieser Klient ist nicht geschlossen.',
  },
  not_closable: {
    key: 'errors.not_closable',
    de: 'Nur Verkäufer können geschlossen werden.',
  },
  close_reason_required: {
    key: 'errors.close_reason_required',
    de: 'Bitte geben Sie einen Grund an (mindestens 5 Zeichen).',
  },
  direct_owner_change_blocked: {
    key: 'errors.direct_owner_change_blocked',
    de: 'Makler-Wechsel ist hier nicht möglich (Vertrag unterschrieben oder Sperre aktiv).',
  },

  // ── seller rules (second makler / phone mask / compliance) ──
  not_secondary_assignable: {
    key: 'errors.not_secondary_assignable',
    de: 'Ein zweiter Makler kann nur bei Verkäufern zugewiesen werden.',
  },
  compliance_manager_only: {
    key: 'errors.compliance_manager_only',
    de: 'Diese Aktion ist Compliance-Verantwortlichen vorbehalten.',
  },
  secondary_already_set: {
    key: 'errors.secondary_already_set',
    de: 'Für diesen Verkäufer ist bereits ein zweiter Makler zugewiesen.',
  },
  no_secondary_set: {
    key: 'errors.no_secondary_set',
    de: 'Für diesen Verkäufer ist kein zweiter Makler zugewiesen.',
  },
  already_primary: {
    key: 'errors.already_primary',
    de: 'Dieser Makler ist bereits der zuständige Makler.',
  },
  client_opted_out: {
    key: 'errors.client_opted_out',
    de: 'Dieser Klient hat Werbung untersagt (DSGVO-Widerspruch). Die Nachricht wurde nicht gesendet.',
  },
  masked_phone_rejected: {
    key: 'errors.masked_phone_rejected',
    de: 'Maskierte Telefonnummern können nicht gespeichert werden — nur der zuständige Makler sieht und bearbeitet die echte Nummer.',
  },

  // ── Agenten-Verwaltung (/agents) ──
  // Diese Codes hatten bis 2026-09-28 ein eigenes, zweites Wörterbuch im
  // Dialog — und der Bearbeiten-Dialog benutzte es nicht, zeigte also den
  // rohen Slug. Ein Wörterbuch für alle.
  agent_id_invalid_chars: {
    key: 'agents.error.id_invalid_chars',
    de: 'Agent-ID darf nur A–Z und 0–9 enthalten, 4–16 Zeichen.',
  },
  agent_id_taken: {
    key: 'agents.error.id_taken',
    de: 'Diese Agent-ID ist bereits vergeben.',
  },
  agent_id_reserved_for_secret_admin: {
    key: 'agents.error.id_reserved',
    de: 'Diese Agent-ID ist für einen Administrator-Zugang reserviert.',
  },
  super_admin_only_flags: {
    key: 'errors.super_admin_only_flags',
    de: 'Telefonnummern-Zugriff und Verkäufer-Aufsicht darf nur der Haupt-Administrator ändern. Alles andere an diesem Profil können Sie speichern.',
  },
  forbidden_only_admin1_manages_admins: {
    key: 'errors.only_admin1_manages_admins',
    de: 'Administrator-Konten kann nur der Haupt-Administrator verwalten.',
  },
  forbidden_only_admin1_creates_admins: {
    key: 'errors.only_admin1_creates_admins',
    de: 'Administrator-Konten kann nur der Haupt-Administrator anlegen.',
  },
  bootstrap_admin_managed_via_secret: {
    key: 'errors.bootstrap_admin_secret',
    de: 'Dieses Administrator-Konto wird über die Server-Konfiguration verwaltet und lässt sich hier nicht ändern.',
  },
  cannot_change_admin_role: {
    key: 'errors.cannot_change_admin_role',
    de: 'Die Rolle eines Administrator-Kontos kann hier nicht geändert werden.',
  },
  email_invalid: {
    key: 'errors.email_invalid',
    de: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
  },
  agent_must_be_inactive_first: {
    key: 'errors.agent_must_be_inactive_first',
    de: 'Dieser Zugang muss zuerst deaktiviert werden.',
  },

  // ── generic ──
  rate_limit_exceeded: {
    key: 'errors.rate_limited',
    de: 'Zu viele Anfragen in kurzer Zeit. Bitte warten Sie einen Moment und versuchen Sie es erneut.',
  },
  invalid_payload: {
    key: 'errors.invalid_payload',
    de: 'Die Eingabe konnte nicht verarbeitet werden. Bitte prüfen Sie die Felder.',
  },
  internal_server_error: {
    key: 'errors.server',
    de: 'Serverfehler. Bitte versuchen Sie es später erneut.',
  },
  not_found: {
    key: 'errors.not_found',
    de: 'Der angeforderte Eintrag wurde nicht gefunden.',
  },
};

// Browser-level failures never reach the API, so they arrive as engine strings
// ("Failed to fetch" in Chrome, "Load failed" in Safari) rather than our codes.
const NETWORK_RE = /failed to fetch|load failed|networkerror|network request failed/i;

/**
 * @param raw      the `error` field from the API response, or an Error message
 * @param t        i18next translate function
 * @param fallback human sentence used when the code isn't recognised
 */
export function humanizeApiError(raw: string | null | undefined, t: TFunc, fallback: string): string {
  const code = (raw ?? '').trim();
  if (!code) return fallback;

  if (NETWORK_RE.test(code)) {
    return t(
      'errors.network',
      'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.',
    );
  }

  // Upstream Anthropic failures arrive as `anthropic_error_<httpStatus>`
  // (502 wrapper).
  //
  // The two cases need different sentences, because they need different actions
  // from the person reading them. A 429/5xx is weather: the Worker already
  // retried twice, and trying again in a minute genuinely helps. A 400/401/403
  // is not — it means the key is wrong or the account's budget is exhausted, and
  // "please try again in a minute" sends a makler into a loop that cannot
  // succeed while nobody tells the administrator. Zoki has already fallen over
  // on a spend-limit 400 once.
  if (code.startsWith('anthropic_error_')) {
    const status = Number(code.slice('anthropic_error_'.length));
    if (status === 400 || status === 401 || status === 403) {
      return t(
        'errors.ai_account',
        'Der KI-Dienst ist nicht verfügbar — Zugang oder Kontingent des Kontos. Wiederholen hilft hier nicht; bitte informieren Sie die Administration.',
      );
    }
    return t(
      'errors.ai_upstream',
      'Der KI-Dienst ist derzeit überlastet oder nicht erreichbar. Bitte versuchen Sie es in einer Minute erneut.',
    );
  }

  // Better-Auth-Fehler beim Anlegen eines Zugangs reisen als
  // `auth_signup:<original>` — der innere Text ist englisch und kommt nicht
  // von uns, also bleibt er stehen; davor steht ein Satz, der sagt, WAS
  // gescheitert ist.
  if (code.startsWith('auth_signup:')) {
    const inner = code.slice('auth_signup:'.length).replace(/_/g, ' ').trim();
    const lead = t('agents.error.auth_signup', 'Login-Konto konnte nicht erstellt werden');
    return inner ? `${lead}: ${inner}` : lead;
  }

  const entry = CODE_MAP[code];
  if (entry) return t(entry.key, entry.de);

  // Unknown code: still a readable sentence, but keep the code for support.
  return `${fallback} (${code})`;
}
